/* eslint-disable prettier/prettier */
import React from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { CSpinner } from '@coreui/react'
import { useAuth } from '../context/AuthContext'

const PrivateRoute = ({ children, allowedRoles }) => {
  const { isAuthenticated, loading, error, getUserRole } = useAuth()
  const location = useLocation()

  // Loading state
  if (loading) {
    return (
      <div 
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          background: 'linear-gradient(180deg, #0f1419 0%, #1a1f2e 100%)',
          color: '#fff'
        }}
      >
        <CSpinner 
          color="primary" 
          style={{ 
            width: '4rem', 
            height: '4rem',
            marginBottom: '1.5rem',
            color: 'hsl(168 80% 50%)'
          }} 
        />
        <h3 
          style={{ 
            fontFamily: 'Outfit, sans-serif',
            fontWeight: 600,
            fontSize: '1.5rem',
            marginBottom: '0.5rem',
            background: 'linear-gradient(135deg, hsl(168 80% 50%), hsl(200 100% 60%))',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text'
          }}
        >
          Loading
        </h3>
        <p style={{ 
          color: 'rgba(255, 255, 255, 0.5)',
          fontSize: '0.95rem',
          fontFamily: 'Outfit, sans-serif'
        }}>
          Authenticating your session...
        </p>
      </div>
    )
  }

  // Error state
  if (error) {
    return (
      <div 
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          background: 'linear-gradient(180deg, #0f1419 0%, #1a1f2e 100%)',
          color: '#fff',
          padding: '2rem'
        }}
      >
        <div 
          style={{
            background: 'rgba(239, 68, 68, 0.1)',
            border: '1px solid rgba(239, 68, 68, 0.3)',
            borderRadius: '0.75rem',
            padding: '2rem',
            maxWidth: '500px',
            textAlign: 'center'
          }}
        >
          <h3 
            style={{ 
              color: '#fc8181',
              fontFamily: 'Outfit, sans-serif',
              fontWeight: 700,
              fontSize: '1.5rem',
              marginBottom: '1rem'
            }}
          >
            ⚠️ Authentication Error
          </h3>
          <p style={{ 
            color: 'rgba(255, 255, 255, 0.8)',
            fontFamily: 'Outfit, sans-serif',
            marginBottom: '0'
          }}>
            {error.message}
          </p>
        </div>
      </div>
    )
  }

  // Not authenticated - redirect to login
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />
  }

  // Check role-based access if allowedRoles is specified
  if (allowedRoles && allowedRoles.length > 0) {
    const userRole = getUserRole()
    
    if (!userRole || !allowedRoles.includes(userRole)) {
      // User doesn't have permission - show access denied
      return (
        <div 
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100vh',
            background: 'linear-gradient(180deg, #0f1419 0%, #1a1f2e 100%)',
            color: '#fff',
            padding: '2rem'
          }}
        >
          <div 
            style={{
              background: 'rgba(245, 158, 11, 0.1)',
              border: '1px solid rgba(245, 158, 11, 0.3)',
              borderRadius: '0.75rem',
              padding: '2rem',
              maxWidth: '500px',
              textAlign: 'center'
            }}
          >
            <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>🔒</div>
            <h3 
              style={{ 
                color: '#fbbf24',
                fontFamily: 'Outfit, sans-serif',
                fontWeight: 700,
                fontSize: '1.5rem',
                marginBottom: '1rem'
              }}
            >
              Access Denied
            </h3>
            <p style={{ 
              color: 'rgba(255, 255, 255, 0.8)',
              fontFamily: 'Outfit, sans-serif',
              marginBottom: '1rem'
            }}>
              You don't have permission to access this page.
            </p>
            <p style={{ 
              color: 'rgba(255, 255, 255, 0.5)',
              fontFamily: 'Outfit, sans-serif',
              fontSize: '0.875rem',
              marginBottom: '1.5rem'
            }}>
              Your role: <strong style={{ color: '#fff' }}>{userRole || 'Unknown'}</strong>
              <br />
              Required: <strong style={{ color: '#fff' }}>{allowedRoles.join(', ')}</strong>
            </p>
            <button
              onClick={() => window.history.back()}
              style={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                border: 'none',
                borderRadius: '0.5rem',
                color: '#fff',
                padding: '0.75rem 1.5rem',
                fontFamily: 'Outfit, sans-serif',
                fontWeight: 600,
                cursor: 'pointer',
                marginRight: '0.5rem'
              }}
            >
              Go Back
            </button>
            <button
              onClick={() => window.location.href = '/main/dashboard'}
              style={{
                background: 'rgba(255, 255, 255, 0.1)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                borderRadius: '0.5rem',
                color: '#fff',
                padding: '0.75rem 1.5rem',
                fontFamily: 'Outfit, sans-serif',
                fontWeight: 600,
                cursor: 'pointer'
              }}
            >
              Dashboard
            </button>
          </div>
        </div>
      )
    }
  }

  return children
}

export default PrivateRoute